import {allFromSnapshot, FirestoreAdapter, FirestoreConnection} from "@buildwithflux/firestore-compatibility-layer";
import type {AnyPcbNode, PcbNodesMap} from "@buildwithflux/models";
import {
    AutoLayoutJob,
    AutoLayoutStatus,
    IChatMessageData,
    IChatMessageThreadData,
    IUserData,
} from "@buildwithflux/models";
import {ChatRepository, DocumentPricingRepository, SubscriptionManager} from "@buildwithflux/repositories";
import {Logger, Unsubscriber} from "@buildwithflux/shared";
import {add as addPrice, allocate as allocatePrice, dinero, toDecimal as priceToDecimal} from "dinero.js";
import {chunk, countBy, keyBy} from "lodash";

import {calculateExecutionPlanStatus} from "modules/copilot-execution-plan";

export enum ProjectDataType {
    AutoLayoutAgentStatus = "autoLayoutAgentStatus",
    AutoPilotAgentStatus = "autoPilotAgentStatus",
    CopilotThreadCount = "copilotThreadCount",
    KnowledgeSuggestionCount = "knowledgeSuggestionCount",
    ElementCount = "elementCount",
    NetCount = "netCount",
    SymbolCount = "symbolCount",
    AveragePrice = "averagePrice",
    LastMessageAt = "lastMessageAt",
    PcbLayoutNodes = "pcbLayoutNodes",
}

export type ProjectDataPayload =
    | {
          type: ProjectDataType.AutoLayoutAgentStatus;
          jobUid: string | undefined;
          status: AgentDisplayStatus | undefined;
      }
    | {
          type: ProjectDataType.AutoPilotAgentStatus;
          threadUid: string | undefined;
          status: AgentDisplayStatus | undefined;
      }
    | {type: ProjectDataType.CopilotThreadCount; count: number}
    | {type: ProjectDataType.KnowledgeSuggestionCount; count: number}
    | {type: ProjectDataType.ElementCount; count: number}
    | {type: ProjectDataType.NetCount; count: number}
    | {type: ProjectDataType.SymbolCount; count: number}
    | {type: ProjectDataType.AveragePrice; price: number | null}
    | {type: ProjectDataType.LastMessageAt; timestamp: number | null}
    | {
          type: ProjectDataType.PcbLayoutNodes;
          nodes: PcbNodesMap<AnyPcbNode>;
      };

export enum AgentDisplayStatus {
    // AutoLayout statuses
    AutoLayoutInitializing = "autoLayout_initializing",
    AutoLayoutRouting = "autoLayout_routing",
    AutoLayoutPaused = "autoLayout_paused",
    AutoLayoutResuming = "autoLayout_resuming",
    AutoLayoutComplete = "autoLayout_complete",
    AutoLayoutFailed = "autoLayout_failed",

    // Autopilot statuses
    AutopilotUpdatingPlan = "autopilot_updating_plan",
    AutopilotCreatingPlan = "autopilot_creating_plan",
    AutopilotProcessing = "autopilot_processing",
    AutopilotReadyToExecute = "autopilot_ready_to_execute",
    AutopilotWorking = "autopilot_working",
    AutopilotCompleted = "autopilot_completed",
    AutopilotAmbiguous = "autopilot_ambiguous",
    AutopilotWaitingForInput = "autopilot_waiting_for_input",
    AutopilotImpossible = "autopilot_impossible",
    AutopilotFailed = "autopilot_failed",
    AutopilotStopping = "autopilot_stopping",
}

interface SubscriptionBatchData {
    /** Set of project UIDs in this batch */
    projectUids: ReadonlyArray<string>;

    /** Unsubscriber function for the entire batch subscription */
    unsubscriber: Unsubscriber;
}

/**
 * Service that provides batched real-time subscriptions to project data for enriching project cards.
 * Subscribes to auto-layout jobs, autopilot threads, and knowledge suggestions for multiple projects
 * efficiently using Firestore batch queries.
 */
export class ProjectDataEnrichmentService<F extends FirestoreConnection = FirestoreConnection> {
    private readonly subscriptionManager: SubscriptionManager<string, ProjectDataPayload>;

    private readonly subscriptionBatchesMap: Map<string, SubscriptionBatchData> = new Map();

    constructor(
        private readonly firestoreAdapter: FirestoreAdapter<F>,
        private readonly chatRepository: ChatRepository,
        private readonly documentPricingRepository: DocumentPricingRepository,
        private readonly logger: Logger,
    ) {
        this.subscriptionManager = new SubscriptionManager({
            sendLatestNotifiedValueOnSubscribe: true,
        });
    }

    /**
     * Sets up batched Firestore subscriptions for the given projects.
     *
     * Creates efficient batch subscriptions (10 projects per batch) for auto-layout and
     * autopilot status updates. Safe to call multiple times - skips existing subscriptions.
     *
     * Call `subscribe()` to receive status updates for individual projects.
     */
    public setupSubscriptions(userData: IUserData, projectUids: string[]): void {
        const newProjectUids = projectUids.filter((uid) => !this.subscriptionBatchesMap.has(uid));

        if (newProjectUids.length === 0) {
            return;
        }

        // Batch subscriptions in chunks of 10 (Firestore 'in' query limit)
        const BATCH_SIZE = 10;
        const batches = chunk(newProjectUids, BATCH_SIZE);
        for (const batch of batches) {
            this.setupBatchSubscription(userData, batch);
        }
    }

    /**
     * Subscribes to data updates for a specific project.
     *
     * Receives real-time updates for the specified data type (project status, thread count, or knowledge suggestion count).
     * Callback is invoked immediately with latest data, then on each change.
     *
     * Ensure `setupSubscriptions()` has been called first for this project.
     * Automatically cleans up batch subscriptions when all subscribers unsubscribe.
     */
    public subscribe<T extends ProjectDataType, P = Extract<ProjectDataPayload, {type: T}>>(
        projectUid: string,
        dataType: T,
        callback: (payload: P) => void,
    ): Unsubscriber {
        const unsub = this.subscriptionManager.addSubscription(projectUid, (payload) => {
            if (payload.type === dataType) {
                callback(payload as P);
            }
        });

        return () => {
            unsub();

            const batch = this.subscriptionBatchesMap.get(projectUid);
            if (!batch) {
                // Should ideally not happen, but safeguard just in case
                return;
            }

            const {projectUids, unsubscriber} = batch;

            // If no more listeners for any project in the batch, unsubscribe from the batch
            if (projectUids.every((uid) => this.subscriptionManager.listenerCount(uid) === 0)) {
                unsubscriber();

                for (const uid of projectUids) {
                    this.subscriptionBatchesMap.delete(uid);
                }
            }
        };
    }

    private setupBatchSubscription(userData: IUserData, projectUids: string[]): void {
        const unsubs: Unsubscriber[] = [];

        // 1. Batch auto-layout jobs subscription
        const autoLayoutUnsub = this.subscribeToAutoLayoutJobs(userData.handle, projectUids);
        unsubs.push(autoLayoutUnsub);

        // 2. Individual autopilot threads subscriptions
        for (const projectUid of projectUids) {
            const autopilotUnsub = this.subscribeToAutopilotThreads(projectUid);
            unsubs.push(autopilotUnsub);
        }

        // 3. Batch knowledge suggestions subscription
        const knowledgeSuggestionsUnsub = this.subscribeToKnowledgeSuggestions(userData.uid, projectUids);
        unsubs.push(knowledgeSuggestionsUnsub);

        // 4. Individual element, net, symbol, structure type, and pricing subscriptions
        for (const projectUid of projectUids) {
            unsubs.push(this.subscribeToElements(projectUid));
            unsubs.push(this.subscribeToNets(projectUid));
            unsubs.push(this.subscribeToSymbols(projectUid));
            unsubs.push(this.subscribeToPcbLayoutNodes(projectUid));
            unsubs.push(this.subscribeToPricing(projectUid));
        }

        const batchData: SubscriptionBatchData = {
            projectUids: [...projectUids],
            unsubscriber: () => {
                for (const unsub of unsubs) {
                    unsub();
                }
            },
        };

        for (const uid of projectUids) {
            this.subscriptionBatchesMap.set(uid, batchData);
        }
    }

    private subscribeToAutoLayoutJobs(userHandle: string, projectUids: string[]): Unsubscriber {
        // Subscribe to active auto-layout jobs for the given projects
        return this.firestoreAdapter
            .userJobsCollection(userHandle)
            .where("type", "==", "autoLayout")
            .where("projectUid", "in", projectUids)
            .onSnapshot(
                (snapshot) => {
                    for (const change of snapshot.docChanges()) {
                        const job = change.doc.data();
                        if (job.type === "autoLayout") {
                            // Clear status when job is removed
                            if (change.type === "removed") {
                                this.subscriptionManager.notify(job.projectUid, {
                                    type: ProjectDataType.AutoLayoutAgentStatus,
                                    jobUid: job.jobUid,
                                    status: undefined,
                                });
                            } else {
                                const status = this.getAutoLayoutJobDisplayStatus(job);

                                this.subscriptionManager.notify(job.projectUid, {
                                    type: ProjectDataType.AutoLayoutAgentStatus,
                                    jobUid: job.jobUid,
                                    status,
                                });
                            }
                        }
                    }
                },
                (error) => {
                    this.logger.error("Error subscribing to auto-layout jobs", error);

                    for (const projectUid of projectUids) {
                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.AutoLayoutAgentStatus,
                            jobUid: undefined,
                            status: undefined,
                        });
                    }
                },
            );
    }

    private subscribeToAutopilotThreads(projectUid: string): Unsubscriber {
        /**
         * Track latestActiveThread outside the subscription to avoid race conditions:
         * If a new snapshot arrives while getMessages() is in flight, we need to ensure
         * the status notification uses the correct thread data without being overwritten
         * by stale data from the previous snapshot's async operation.
         */
        let latestActiveThread: IChatMessageThreadData | undefined = undefined;

        // Subscribe to all chat threads for this project
        return this.firestoreAdapter.documentChatThreadsCollection(projectUid).onSnapshot(
            (snapshot) => {
                const threads = snapshot.docs.map((doc) => doc.data());
                const threadCount = threads.length;

                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.CopilotThreadCount,
                    count: threadCount,
                });

                // Find the most recent thread with a brief
                latestActiveThread = threads.reduce<IChatMessageThreadData | undefined>((latest, thread) => {
                    if (!thread.latest_brief) {
                        return latest;
                    }

                    if (!latest) {
                        return thread;
                    }

                    if (thread.created_at > latest.created_at) {
                        return thread;
                    }

                    return latest;
                }, undefined);

                if (!latestActiveThread) {
                    this.subscriptionManager.notify(projectUid, {
                        type: ProjectDataType.AutoPilotAgentStatus,
                        threadUid: undefined,
                        status: undefined,
                    });
                    this.subscriptionManager.notify(projectUid, {
                        type: ProjectDataType.LastMessageAt,
                        timestamp: null,
                    });
                    return;
                }

                // Fetch messages to determine execution plan status and lastMessageAt
                const threadUidToFetch = latestActiveThread.uid;
                this.chatRepository
                    .getMessages(projectUid, threadUidToFetch)
                    .then((messages) => {
                        // Race condition prevention
                        if (!latestActiveThread || latestActiveThread.uid !== threadUidToFetch) {
                            return;
                        }

                        const status = this.getAutopilotThreadDisplayStatus(latestActiveThread, messages ?? []);

                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.AutoPilotAgentStatus,
                            threadUid: threadUidToFetch,
                            status,
                        });

                        const lastMessageTimestamp =
                            messages && messages.length > 0 ? Math.max(...messages.map((msg) => msg.created_at)) : null;

                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.LastMessageAt,
                            timestamp: lastMessageTimestamp,
                        });
                    })
                    .catch((error) => {
                        this.logger.error(
                            `Error fetching messages for thread ${latestActiveThread?.uid} in project ${projectUid}`,
                            error,
                        );

                        // Race condition prevention
                        if (!latestActiveThread || latestActiveThread.uid !== threadUidToFetch) {
                            return;
                        }

                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.AutoPilotAgentStatus,
                            threadUid: threadUidToFetch,
                            status: undefined,
                        });
                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.LastMessageAt,
                            timestamp: null,
                        });
                    });
            },
            (error) => {
                this.logger.error(`Error subscribing to autopilot threads for project ${projectUid}`, error);
            },
        );
    }

    private subscribeToKnowledgeSuggestions(userUid: string, projectUids: string[]): Unsubscriber {
        // Subscribe to pending knowledge suggestions for the given projects
        return this.firestoreAdapter
            .knowledgeBaseSuggestionsCollection()
            .where("user_uid", "==", userUid)
            .where("project_uid", "in", projectUids)
            .where("status", "==", "pending")
            .onSnapshot(
                (snapshot) => {
                    const unnotifiedProjectUidsSet = new Set(projectUids);

                    const suggestions = allFromSnapshot(snapshot);
                    const countsByProjectUid = countBy(suggestions, (suggestion) => suggestion.project_uid);

                    for (const [projectUid, count] of Object.entries(countsByProjectUid)) {
                        this.subscriptionManager.notify(projectUid, {
                            type: ProjectDataType.KnowledgeSuggestionCount,
                            count,
                        });

                        unnotifiedProjectUidsSet.delete(projectUid);
                    }

                    // Notify remaining projects with 0 pending suggestions
                    for (const unnnotifiedProjectUid of unnotifiedProjectUidsSet) {
                        this.subscriptionManager.notify(unnnotifiedProjectUid, {
                            type: ProjectDataType.KnowledgeSuggestionCount,
                            count: 0,
                        });
                    }
                },
                (error) => {
                    this.logger.error("Error subscribing to knowledge suggestions", error);
                },
            );
    }

    private subscribeToElements(projectUid: string): Unsubscriber {
        return this.firestoreAdapter.documentElementsCollection(projectUid).onSnapshot(
            (snapshot) => {
                const uniquePartUids = new Set(snapshot.docs.map((doc) => doc.data().part_uid));

                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.ElementCount,
                    count: uniquePartUids.size,
                });
            },
            (error) => {
                this.logger.error(`Error subscribing to elements for project ${projectUid}`, error);
            },
        );
    }

    private subscribeToNets(projectUid: string): Unsubscriber {
        return this.firestoreAdapter.documentNetsCollection(projectUid).onSnapshot(
            (snapshot) => {
                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.NetCount,
                    count: snapshot.docs.length,
                });
            },
            (error) => {
                this.logger.error(`Error subscribing to nets for project ${projectUid}`, error);
            },
        );
    }

    private subscribeToSymbols(projectUid: string): Unsubscriber {
        return this.firestoreAdapter.documentAssetsCollection(projectUid).onSnapshot(
            (snapshot) => {
                const uniqueSymbols = new Set(
                    snapshot.docs
                        .map((doc) => doc.data().associatedSymbol)
                        .filter((symbol): symbol is string => symbol !== undefined && symbol !== ""),
                );

                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.SymbolCount,
                    count: uniqueSymbols.size,
                });
            },
            (error) => {
                this.logger.error(`Error subscribing to symbols for project ${projectUid}`, error);
            },
        );
    }

    private subscribeToPricing(projectUid: string): Unsubscriber {
        return this.documentPricingRepository.subscribeToLatestPricing(projectUid, (pricing) => {
            if (!pricing) {
                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.AveragePrice,
                    price: null,
                });
                return;
            }

            const minCost = dinero(pricing.bomCost.minCost);
            const maxCost = dinero(pricing.bomCost.maxCost);
            const [averageCost] = allocatePrice(addPrice(minCost, maxCost), [1, 1]);
            const averagePrice = averageCost ? parseFloat(priceToDecimal(averageCost)) : null;

            this.subscriptionManager.notify(projectUid, {
                type: ProjectDataType.AveragePrice,
                price: averagePrice,
            });
        });
    }

    private subscribeToPcbLayoutNodes(projectUid: string): Unsubscriber {
        return this.firestoreAdapter.documentPcbLayoutNodesCollection(projectUid).onSnapshot(
            (snapshot) => {
                const nodes: PcbNodesMap<AnyPcbNode> = {};
                for (const doc of snapshot.docs) {
                    const node = doc.data();
                    nodes[node.uid] = node;
                }

                this.subscriptionManager.notify(projectUid, {
                    type: ProjectDataType.PcbLayoutNodes,
                    nodes,
                });
            },
            (error) => {
                this.logger.error(`Error subscribing to PCB layout nodes for project ${projectUid}`, error);
            },
        );
    }

    private getAutoLayoutJobDisplayStatus(job: AutoLayoutJob): AgentDisplayStatus | undefined {
        switch (job.jobStatus) {
            case AutoLayoutStatus.allocated:
            case AutoLayoutStatus.populated:
            case AutoLayoutStatus.submitted:
            case AutoLayoutStatus.ready:
            case AutoLayoutStatus.booting:
                return AgentDisplayStatus.AutoLayoutInitializing;

            case AutoLayoutStatus.started:
            case AutoLayoutStatus.restarted:
            case AutoLayoutStatus.generating:
                return AgentDisplayStatus.AutoLayoutRouting;

            case AutoLayoutStatus.paused:
            case AutoLayoutStatus.outOfCredit:
                return AgentDisplayStatus.AutoLayoutPaused;

            case AutoLayoutStatus.resuming:
                return AgentDisplayStatus.AutoLayoutResuming;

            case AutoLayoutStatus.converged:
            case AutoLayoutStatus.timeout:
                return AgentDisplayStatus.AutoLayoutComplete;

            case AutoLayoutStatus.failed:
                return AgentDisplayStatus.AutoLayoutFailed;

            case AutoLayoutStatus.pausing:
            case AutoLayoutStatus.discarded:
            case AutoLayoutStatus.applied:
                return undefined;
        }
    }

    private getAutopilotThreadDisplayStatus(
        thread: IChatMessageThreadData,
        messages: IChatMessageData[],
    ): AgentDisplayStatus | undefined {
        const latestBrief = thread.latest_brief;
        const hasBrief = !!latestBrief;

        const executionPlanStatus = calculateExecutionPlanStatus(latestBrief, keyBy(messages, "uid"));

        switch (executionPlanStatus) {
            case "generating":
                return hasBrief ? AgentDisplayStatus.AutopilotUpdatingPlan : AgentDisplayStatus.AutopilotCreatingPlan;

            case "processing":
                return AgentDisplayStatus.AutopilotProcessing;

            case "ready_to_execute":
                return AgentDisplayStatus.AutopilotReadyToExecute;

            case "working":
                return AgentDisplayStatus.AutopilotWorking;

            case "completed":
                return AgentDisplayStatus.AutopilotCompleted;

            case "ambiguous":
                return AgentDisplayStatus.AutopilotAmbiguous;

            case "waiting_for_input":
                return AgentDisplayStatus.AutopilotWaitingForInput;

            case "impossible":
                return AgentDisplayStatus.AutopilotImpossible;

            case "failed":
                return AgentDisplayStatus.AutopilotFailed;

            case "stopping":
                return AgentDisplayStatus.AutopilotStopping;

            case "discard":
            case "stopped":
            case "ready":
            default:
                return undefined;
        }
    }
}
